import React, { FC, useEffect, useRef, useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import { alpha, Theme, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { CheckFilledIcon, CloseFilledIcon, CloudUploadIcon, Modal } from 'src/shared/components';
import { ConfirmDeleteFileModal } from 'src/shared/components/Modal';
import { ModalFooterProps } from 'src/shared/components/Modal/ModalFooter';
import { ModalHeaderProps } from 'src/shared/components/Modal/ModalHeader';
import { useTranslate } from 'src/shared/providers';
import { RootState } from 'src/store/types';

import { FileChip } from './FileThumbnailAndChip';

interface Props {
  open: boolean;
  files: File[];
  setFiles: (files: File[]) => void;
  onClose: () => void;
  onDeleteFile?: (file: File) => void;
  loading?: boolean;
  maxFiles?: number;
  closeOnSubmit?: boolean;
  confirmOnDelete?: boolean;
}

const MAX_FILES = 3;
const MAX_FILE_SIZE_IN_MB = 20;
const BYTES_IN_MB = 1024 * 1024;

export const ALLOWED_EXTENSIONS = [
  'bmp',
  'doc',
  'docx',
  'gif',
  'jpeg',
  'jpg',
  'pdf',
  'png',
  'rtf',
  'tif',
  'tiff',
  'txt',
  'xls',
  'xlsx',
];

enum ErrorCode {
  FileTooLarge = 'file-too-large',
  FileInvalidType = 'file-invalid-type',
  TooManyFiles = 'too-many-files',
}

export const DocumentUploadModal: FC<Props> = ({
  open,
  maxFiles = MAX_FILES,
  onClose,
  loading,
  files = [],
  setFiles,
  onDeleteFile,
  closeOnSubmit,
  confirmOnDelete = false,
}) => {
  const { t } = useTranslate();
  const theme = useTheme();

  const [localFiles, setLocalFiles] = useState<File[]>(files);
  const [deleteFileModalOpen, setDeleteFileModalOpen] = useState(false);
  const [deleteFile, setDeleteFile] = useState<File | null>(null);

  useEffect(() => {
    setLocalFiles(files);
  }, [files]);

  const { settings } = useSelector((state: RootState) => state.user);
  const employeeDocumentFileSizeLimitInMb = settings?.employeeDocumentFileSizeLimit
    ? settings.employeeDocumentFileSizeLimit / BYTES_IN_MB
    : MAX_FILE_SIZE_IN_MB;

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleFilesSelected = (selectedFiles: File[]) => {
    const accepted: File[] = [];

    if (localFiles.length + selectedFiles.length > maxFiles) {
      toast.info(t('messageCenter.youCanUploadUpToMaxfilesFilesOnly', { maxFiles: maxFiles }), {
        type: toast.TYPE.ERROR,
      });
      return;
    }

    selectedFiles.forEach((file) => {
      const errors = validateFile(
        file,
        localFiles.length + accepted.length,
        maxFiles,
        employeeDocumentFileSizeLimitInMb,
      );
      if (errors.length) {
        errors.forEach((err) => {
          switch (err) {
            case ErrorCode.FileTooLarge:
              toast.info(
                t('files.yourFileSizeOfLimitMbExceedsTheSizeLimit', {
                  limit: (file.size / BYTES_IN_MB).toFixed(2),
                  size: employeeDocumentFileSizeLimitInMb,
                }),
                { type: toast.TYPE.ERROR },
              );
              break;
            case ErrorCode.FileInvalidType:
              toast.info(t('files.yourFileTypeIsNotAllowed'), { type: toast.TYPE.ERROR });
              break;
            case ErrorCode.TooManyFiles:
              toast.info(t('messageCenter.youCanUploadUpToMaxfilesFilesOnly', { maxFiles: maxFiles }), {
                type: toast.TYPE.ERROR,
              });
              break;
            default:
              toast.info(t('files.somethingWentWrongWhenUploadingYourFilePleaseTryAgain'), { type: toast.TYPE.ERROR });
              break;
          }
        });
      } else {
        accepted.push(file);
      }
    });

    if (accepted.length) setLocalFiles((prev) => [...prev, ...accepted]);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    handleFilesSelected(Array.from(e.target.files));
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (loading) return;
    handleFilesSelected(Array.from(e.dataTransfer.files));
  };

  const prevent = (e: React.DragEvent) => e.preventDefault();

  const handleSubmit = () => {
    if (!localFiles.length) return;
    setFiles(localFiles);
    if (closeOnSubmit) {
      onClose();
    }
  };

  const getDropzoneBackgroundColor = (theme: Theme) => {
    if (loading) {
      return theme.palette.mode === 'dark' ? theme.palette.secondary.dark : theme.palette.secondary.light;
    }

    if (localFiles.length === maxFiles) {
      return alpha(theme.palette.success.main, 0.2);
    }

    return theme.palette.mode === 'dark' ? '#191a1f' : '#f8f9fc';
  };

  const getDropzoneMessage = () => {
    if (loading) {
      return t('global.uploadingFile');
    }

    return t('global.draganddropFileOrBrowseComputer');
  };

  const addModalHeaderProps: ModalHeaderProps = {
    title: maxFiles > 1 ? t('messageCenter.addAttachments') : t('files.addAttachment'),
    onClose,
  };

  const addModalFooterProps: ModalFooterProps = {
    submitText: t('global.upload'),
    submitIcon: <CheckFilledIcon />,
    onSubmit: handleSubmit,
    disabled: !localFiles.length,
    loading: loading,
    closeText: t('global.cancel'),
    closeIcon: <CloseFilledIcon />,
    onClose: () => {
      onClose();
      setLocalFiles([]);
      setFiles(files);
    },
  };

  // Convert to MIME/file extension string for input accept attribute
  const acceptAttr = ALLOWED_EXTENSIONS.map((ext) => `.${ext}`).join(',');

  const handleDeleteFile = (file?: File) => {
    if (!file) {
      return;
    }

    if (confirmOnDelete) {
      setDeleteFile(file);
      setDeleteFileModalOpen(true);
    } else {
      onDeleteFile?.(file);
      setLocalFiles(localFiles.filter((f) => f.name !== file.name && f.size !== file.size && f.type !== file.type));
    }
  };

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        modalHeaderProps={addModalHeaderProps}
        modalFooterProps={addModalFooterProps}
      >
        <Stack direction="column" gap={2}>
          <Stack
            flexGrow={1}
            direction="column"
            alignItems="center"
            gap={2}
            sx={{
              padding: 4,
              border: (theme) => `1px dashed ${theme.palette.divider}`,
              borderRadius: 1,
              backgroundColor: getDropzoneBackgroundColor(theme),
              cursor: localFiles.length >= maxFiles || loading ? 'not-allowed' : 'pointer',
              opacity: localFiles.length >= maxFiles ? 0.5 : 1,
            }}
            onClick={() => localFiles.length < maxFiles && !loading && fileInputRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={prevent}
            onDragEnter={prevent}
          >
            <input
              type="file"
              multiple
              ref={fileInputRef}
              onChange={handleFileInputChange}
              style={{ display: 'none' }}
              accept={acceptAttr}
            />
            {loading ? (
              <CircularProgress size={32} />
            ) : (
              <CloudUploadIcon size={32} color={theme.palette.text.secondary} />
            )}
            {localFiles.length < maxFiles ? (
              <>
                <Typography variant="caption" fontWeight="bold">
                  {getDropzoneMessage()}
                </Typography>
                {!loading && (
                  <Typography variant="caption" color={theme.palette.text.disabled}>
                    {maxFiles > 1
                      ? t('messageCenter.uploadUpToMaxfilesFiles', { maxFiles: maxFiles })
                      : t('files.uploadUpToMaxfilesFile', { maxFiles: maxFiles })}
                  </Typography>
                )}
              </>
            ) : (
              <>
                <Typography variant="caption" color={theme.palette.text.secondary}>
                  {t('files.fileSReadyForUpload', { count: localFiles.length })}
                </Typography>
              </>
            )}
          </Stack>

          {localFiles.map((file) => (
            <FileChip key={file.name} file={file} onDeleteFile={handleDeleteFile} />
          ))}
        </Stack>
      </Modal>
      {confirmOnDelete && (
        <ConfirmDeleteFileModal
          open={deleteFileModalOpen}
          onClose={() => {
            setDeleteFileModalOpen(false);
            setDeleteFile(null);
          }}
          onConfirm={() => {
            if (!deleteFile) {
              return;
            }
            onDeleteFile?.(deleteFile);
            setLocalFiles((prev) => prev.filter((f) => f !== deleteFile));
            setDeleteFileModalOpen(false);
            setDeleteFile(null);
          }}
          fileName={deleteFile?.name || ''}
        />
      )}
    </>
  );
};

// --- Validation function (Separation of Concern) ---
const validateFile = (file: File, existingFilesCount: number, maxFiles: number, maxSizeInMb: number): ErrorCode[] => {
  const errors: ErrorCode[] = [];

  if (file.size > maxSizeInMb * 1_000_000) errors.push(ErrorCode.FileTooLarge);

  const ext = file.name.split('.').pop()?.toLowerCase();

  if (!ext || !ALLOWED_EXTENSIONS.includes(ext)) errors.push(ErrorCode.FileInvalidType);

  if (existingFilesCount + 1 > maxFiles) errors.push(ErrorCode.TooManyFiles);

  return errors;
};
